import { createDeferredData, DeferredData } from '../../../infrastructure/adapters/DeferredDataAdapter';
import { useGodMode, GodModeData } from '../hooks/useGodMode';

export interface GodModeMetrics {
  totalTasks: number;
  activeTasks: number;
  completedTasks: number;
  overdueTasks: number;
  totalDepartments: number;
  totalEmployees: number;
  productivityScore: number;
}

export type GodModeQueryAdapterResult = {
  data?: DeferredData<GodModeMetrics>;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  refetch: () => void;
};

// Selectors
export const selectGodModeMetrics = (data: GodModeData): GodModeMetrics => {
  const allTasks = data.tasks || [];
  const allEmps = data.users || [];

  const totalTasks = allTasks.length;
  const activeTasks = allTasks.filter(t => t.status !== 'done').length;
  const completedTasks = allTasks.filter(t => t.status === 'done').length;
  
  const now = new Date().toISOString().split('T')[0];
  const overdueTasks = allTasks.filter(t => t.due_date && t.due_date < now && t.status !== 'done').length;

  const depts = new Set(allEmps.map(e => e.department).filter(Boolean));
  const totalDepartments = depts.size;
  const totalEmployees = allEmps.length;

  const productivityScore = totalTasks 
    ? Math.round((completedTasks / totalTasks) * 100) 
    : 0;

  return {
    totalTasks,
    activeTasks,
    completedTasks,
    overdueTasks,
    totalDepartments,
    totalEmployees,
    productivityScore
  };
};

export function useGodModeAdapter(): GodModeQueryAdapterResult {
  const query = useGodMode(selectGodModeMetrics);
  
  const data = query.data !== undefined 
    ? createDeferredData<GodModeMetrics>(query.data) 
    : undefined;
  
  return { ...query, data, refetch: query.refetch as () => void };
}
